/**
 * DomainBundleItem — card in the domain bundle list (middle column).
 *
 * Uses DomainCard with compact density and left accent. Shows domain name,
 * email counts, urgency, time-ago, and the most urgent email subject.
 */
import React from 'react';
import { cn, DomainCard } from '../../../ui';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { TimeAgo } from '../shared/TimeAgo';

function DomainBundleItem({ bundle, selected, onClick }) {
  // Show the most urgent/latest email subject as the preview line
  const topSubject = bundle.topicGroups?.[0]?.label || '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={cn(
        'cursor-pointer transition-shadow rounded-lg',
        selected ? 'ring-1 ring-aqua shadow-sm' : 'hover:shadow-sm',
      )}
    >
      <DomainCard domain={bundle.domainKey} density="compact" accentPlacement="left">
        <div className="flex flex-col gap-0.5 min-w-0">
          {/* Top row — domain name + counts */}
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-body font-semibold text-text-dark truncate min-w-0">
              {bundle.domainLabel}
            </span>
            <div className="flex items-center gap-1 ml-auto shrink-0">
              {bundle.unreadCount > 0 && (
                <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full bg-red-critical text-white">
                  {bundle.unreadCount}
                </span>
              )}
              <span className="text-body-sm text-text-muted">
                {bundle.totalCount}
              </span>
            </div>
          </div>

          {/* Middle row — urgency + time */}
          <div className="flex items-center gap-1.5 min-w-0">
            <UrgencyIndicator urgency={bundle.highestUrgency} />
            <TimeAgo date={bundle.latestReceivedAt} className="ml-auto !text-caption" />
          </div>

          {/* Bottom row — most urgent subject */}
          <div className="text-caption text-text-muted truncate min-w-0 mt-0.5">
            {topSubject}
          </div>
        </div>
      </DomainCard>
    </div>
  );
}

export { DomainBundleItem };
